import HeaderPortal from "components/portal/header-portal";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "ui/components/form";
import ButtonWithLoading from "@/components/button-with-loading";
import { Form } from "react-crud-form";
import { Input } from "ui/components/input";
import { Textarea } from "ui/components/textarea";
import { timesheetApprovalSchema } from "./schema";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { useHotkeys } from "react-hotkeys-hook";
import { useNavigate } from "react-router-dom";
import { useUpdateTimesheetEntry } from "components/timesheet/review/hooks/useUpdateTimesheetEntry";
import { zodResolver } from "@hookform/resolvers/zod";
import { invalidateAllQueries } from "utils/utils";

export default function TimesheetReviewForm({ entryData, date }) {
  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(timesheetApprovalSchema),
    defaultValues: {
      date: date,
      approved_hours: entryData?.approved_hours ?? entryData?.logged_hours ?? "",
      unapproved_hours: entryData?.unapproved_hours ?? 0,
      reason: entryData?.reason ?? "",
    },
    context: {
      logged_hours: entryData?.logged_hours || 0,
      date,
    },
  });

  const updateTimesheetMutation = useUpdateTimesheetEntry({
    onSuccess: () => {
      toast.success("Timesheet reviewed successfully");
      invalidateAllQueries();
    },
    onError: (error) => {
      // Handle specific error cases
      if (error.response?.status === 403) {
        toast.error(
          "Entry Not found, It may have been updated or removed recently",
        );
        // window.location.reload();
      } else {
        toast.error("Failed to review timesheet");
      }
      console.error("Error updating timesheet:", error);
      throw error;
    },
  });

  const handleUpdate = async (id, context) => {
    try {
      await updateTimesheetMutation.mutateAsync({
        id,
        values: context.values,
      });
    } catch (error) {
      // Error handling is now done in the mutation
      throw error;
    }
  };

  function handleClose() {
    navigate(-1);
  }

  return (
    <Form
      form={form}
      schema={timesheetApprovalSchema}
      itemId={entryData.id}
      update={handleUpdate}
    >
      {({ isUpdating, updateItem }) => {
        useHotkeys(
          "ctrl+s, cmd+s",
          (event) => {
            event.preventDefault();
            if (!isUpdating) {
              updateItem().then(handleClose);
            }
          },
          { preventDefault: true },
        );
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 max-w-120">
              <FormField
                control={form.control}
                name="approved_hours"
                render={({ field }) => {
                  const loggedHours = entryData?.logged_hours || 0;
                  return (
                    <FormItem>
                      <FormLabel>Approved Hours</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="ex. 0.5"
                          step="0.5"
                          max={loggedHours}
                          min={0}
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "") {
                              field.onChange(e);
                              return;
                            }
                            if (value < 0 || (value * 10) % 5 !== 0) {
                              e.target.value = "";
                              return;
                            }
                            field.onChange(e);
                            // const value = parseFloat(e.target.value) || 0;
                            const approved = Math.min(value, loggedHours);
                            field.onChange(approved);

                            form.setValue(
                              "unapproved_hours",
                              loggedHours - approved,
                              {
                                shouldDirty: true,
                                shouldValidate: true,
                                shouldTouch: true,
                              }, // Ensure this field is also marked as dirty if needed
                            );
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="unapproved_hours"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel>Unapproved Hours</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          disabled
                          value={field.value}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter reason..."
                      className="max-w-120"
                      {...field}
                      value={field.value}
                    />
                  </FormControl>
                  <FormDescription className="max-w-120">
                    Please provide a clear reason for your decision to help the employee understand and take the appropriate action.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <HeaderPortal>
              <ButtonWithLoading
                variant="default"
                isLoading={isUpdating}
                onClick={() => updateItem().then(handleClose)}
              // disabled={!form.formState.isDirty}
              >
                Save
              </ButtonWithLoading>
            </HeaderPortal>
          </div>
        );
      }}
    </Form>
  );
}
